/**
 * Canonical UTM taxonomy — ADMIN TWIN of backend/src/utils/utm-taxonomy.ts.
 *
 * This is a COPY, on purpose. `frontend/admin` is not a root workspace (root
 * `package.json` workspaces are `apps/*` + `packages/*`), it has its own
 * `package-lock.json`, and CI builds it with a bare `cd frontend/admin &&
 * npm ci && npm run build` — so `@udc/*` and backend paths do not resolve
 * here. Same reasoning, same shape as the two existing precedents:
 * `pages/growth/index.tsx` and `pages/launch-command-center/index.tsx`.
 *
 * A copy with no guard is how the Campaign Links builder starts minting links
 * the backend does not recognise, so the copy is PINNED:
 * `tests/unit/lint/utm-taxonomy-drift.test.ts` parses both files and fails the
 * build the moment they disagree. Editing one side alone is a red CI run, not
 * a silent divergence.
 *
 * 🔴 Adding or removing a value is ONE PR touching docs/marketing/utm-taxonomy.md
 * §2/§3 AND the backend module AND this file (doc §7). Do not "fix" drift by
 * editing only the side that is failing.
 *
 * Keep this module DEPENDENCY-FREE. It is imported by a root-jest test that
 * lives outside `frontend/admin` (jest.config.js `roots` excludes this tree),
 * so any `@/…` alias or npm import here would break the guard that keeps it
 * honest. It is also the reason `Channel` is re-declared below instead of
 * imported.
 */

/**
 * Normalized channel set (docs §5) — mirror of the `Channel` union in
 * backend/src/utils/derive-channel.ts. The backend taxonomy module imports the
 * type from there; the admin bundle cannot reach it, so it is restated. It is
 * a TYPE only, so it costs nothing at runtime and cannot drift silently: the
 * medium map below is what the drift test compares, value by value.
 */
export type Channel =
  | 'organic_social'
  | 'paid_social'
  | 'paid_search'
  | 'organic_search'
  | 'organic_app_store'
  | 'influencer'
  | 'email'
  | 'sms'
  | 'display'
  | 'affiliate'
  | 'referral'
  | 'offline'
  | 'direct'
  | 'unknown';

/**
 * §3 `utm_medium` → derived channel. Keys are lowercased mediums; an absent
 * key is "unmappable" → 'unknown'. Note the non-identity rows: video→paid_social,
 * pr→referral, qr→offline, podcast→referral.
 */
export const MEDIUM_TO_CHANNEL: Readonly<Record<string, Channel>> = Object.freeze({
  organic_social: 'organic_social',
  paid_social: 'paid_social',
  paid_search: 'paid_search',
  influencer: 'influencer',
  email: 'email',
  sms: 'sms',
  display: 'display',
  video: 'paid_social',
  affiliate: 'affiliate',
  referral: 'referral',
  pr: 'referral',
  qr: 'offline',
  podcast: 'referral',
});

/** §3 — the canonical `utm_medium` values, derived from the map above so the
 *  two can never disagree. */
export const UTM_MEDIUMS: readonly string[] = Object.freeze(
  Object.keys(MEDIUM_TO_CHANNEL).sort(),
);

/**
 * §3.1 — paid-medium ALIASES. Ingest-only: the builder never offers them. The
 * backend's deriveChannel() resolves one by the source family below, so
 * facebook/cpc is paid_social and google/cpc is paid_search. Mirrored here so
 * the twin stays a full copy of the vocabulary (drift test).
 */
export const PAID_MEDIUM_ALIASES: readonly string[] = Object.freeze([
  'cpc',
  'paid',
  'paidsearch',
  'ppc',
  'sem',
]);

/** §3.2 — sources that name a SEARCH-ad platform (alias → paid_search). */
export const SEARCH_SOURCE_FAMILY: readonly string[] = Object.freeze([
  'adwords',
  'apple_search_ads',
  'baidu',
  'bing',
  'duckduckgo',
  'google',
  'google_ads',
  'googleads',
  'yahoo',
  'yandex',
]);

/** §3.3 — sources that name a SOCIAL-ad platform (alias → paid_social). */
export const SOCIAL_SOURCE_FAMILY: readonly string[] = Object.freeze([
  'an',
  'facebook',
  'fb',
  'ig',
  'instagram',
  'linkedin',
  'meta',
  'msg',
  'pinterest',
  'reddit',
  'snapchat',
  'threads',
  'tiktok',
  'twitter',
  'x',
  'youtube',
]);

/** §3.4 — social-family sources that Google owns (Google click id → paid_search). */
export const GOOGLE_OWNED_SOCIAL_SOURCES: readonly string[] = Object.freeze(['youtube']);

/**
 * §2 — canonical `utm_source` values a marketer may put on a link.
 * Alphabetical; mirrored verbatim in docs §2.1 and in the backend module.
 */
export const UTM_SOURCES: readonly string[] = Object.freeze([
  'app_store',
  'bing',
  'blog',
  'email',
  'facebook',
  'google',
  'instagram',
  'linkedin',
  'linktree',
  'pinterest',
  'play_store',
  'podcast',
  'pr',
  'qr_print',
  'reddit',
  'sms',
  'snapchat',
  'threads',
  'tiktok',
  'x',
  'youtube',
]);

/**
 * §2 — the two OPEN-ENDED source families. A flat Set-based validator would
 * reject every legitimate influencer and partner link, which is the single
 * easiest way to make this taxonomy useless in practice.
 */
export const SOURCE_PREFIXES: readonly string[] = Object.freeze([
  'influencer_',
  'partner_',
]);

/**
 * §2 — values that are DERIVED channels, never sources. Tagging a link with
 * these corrupts the derivation, so they are called out separately from
 * "unrecognised": seeing one means a human hand-typed a channel name into a
 * link, which is a different bug from a typo.
 */
export const RESERVED_SOURCES: readonly string[] = Object.freeze([
  'direct',
  'referral',
  'organic',
]);

/**
 * Provider-emitted sources. NOT marketer-selectable — these arrive from the
 * Google Play Install Referrer Library and Apple AdAttributionKit on organic
 * installs, always paired with `utm_medium=organic`, and deriveChannel() maps
 * that pair to `organic_app_store` (§5 step 0). They are valid at INGEST and
 * must never be flagged off-taxonomy (that pair is the majority of our android
 * touches), but the campaign-link builder must never offer them.
 */
export const PROVIDER_SOURCES: readonly string[] = Object.freeze([
  'google-play',
  'app-store',
]);

/** The medium half of the provider sentinel pair above. */
export const PROVIDER_MEDIUM = 'organic';

/**
 * Provider "no value" markers. Google Play and GA-style pipelines emit these
 * as LITERAL STRINGS, not NULL — `(not set)` is the one seen in the wild here.
 * Stored verbatim they masquerade as a real source: any coverage metric built
 * on `utm_source IS NOT NULL` counts every one of them as attributed. They are
 * mapped to NULL at ingest (see normalizeTag) because "no referrer" is
 * genuinely the absence of a value, not a value.
 *
 * Compared case-insensitively after trimming, BEFORE whitespace collapsing —
 * `(not set)` contains a space and would otherwise become `(not_set)`.
 */
export const SENTINEL_VALUES: readonly string[] = Object.freeze([
  '(not set)',
  '(none)',
  '(direct)',
  'not set',
  'null',
  'undefined',
]);

/**
 * §4 — `utm_campaign` shape: `YYYYMM_shortname`, lowercase, underscores.
 * Month-anchored so cohorts are obvious from the string alone.
 */
export const CAMPAIGN_SLUG_RE = /^\d{6}_[a-z0-9]+(?:[_-][a-z0-9]+)*$/;

/**
 * Which mediums the builder offers for a given source. This is an EMISSION-TIME
 * constraint only — it stops a human minting `utm_source=google&utm_medium=sms`
 * — and is deliberately NOT applied at ingest, where an unusual-but-real pairing
 * must still be captured rather than flagged.
 *
 * Invariant pinned by the drift test: the union of these lists is exactly
 * UTM_MEDIUMS, so no medium in §3 is unreachable from the builder.
 */
const SOCIAL_MEDIUMS: readonly string[] = ['organic_social', 'paid_social', 'influencer'];

export const SOURCE_MEDIUMS: Readonly<Record<string, readonly string[]>> = Object.freeze({
  instagram: SOCIAL_MEDIUMS,
  tiktok: SOCIAL_MEDIUMS,
  facebook: SOCIAL_MEDIUMS,
  reddit: SOCIAL_MEDIUMS,
  threads: SOCIAL_MEDIUMS,
  snapchat: SOCIAL_MEDIUMS,
  pinterest: SOCIAL_MEDIUMS,
  x: SOCIAL_MEDIUMS,
  linkedin: SOCIAL_MEDIUMS,
  // YouTube is the only social surface where we buy `video` inventory.
  youtube: ['organic_social', 'paid_social', 'video', 'influencer'],
  google: ['paid_search', 'display', 'video'],
  bing: ['paid_search', 'display'],
  email: ['email'],
  sms: ['sms'],
  podcast: ['podcast'],
  blog: ['referral'],
  pr: ['pr'],
  qr_print: ['qr'],
  linktree: ['organic_social', 'referral'],
  app_store: ['referral'],
  play_store: ['referral'],
  // Open-ended families, keyed by their prefix. §2: an influencer link must
  // always be `utm_medium=influencer`.
  'influencer_': ['influencer'],
  'partner_': ['affiliate', 'referral'],
});

/** True when `value` is a provider "no value" marker rather than a real tag. */
export function isSentinelValue(value: string | null | undefined): boolean {
  if (typeof value !== 'string') return false;
  return SENTINEL_VALUES.includes(value.trim().toLowerCase());
}

/**
 * Normalise one UTM tag value to the §1 formatting rules.
 *
 * trim → lowercase → sentinel check → collapse runs of whitespace and `+`
 * into a single `_`.
 *
 * Returns `null` for absent, blank, AND provider-sentinel input — one call is
 * therefore the whole "is there a usable value here?" question, so no caller
 * can normalise and then forget the sentinel. The sentinel test runs BEFORE
 * whitespace collapsing on purpose (`(not set)` → `(not_set)` would slip past).
 *
 * Applies to `utm_*` tags ONLY. Click IDs are opaque, case-significant tokens
 * from the ad platform and must never pass through here.
 */
export function normalizeTag(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const lowered = value.trim().toLowerCase();
  if (lowered.length === 0) return null;
  if (SENTINEL_VALUES.includes(lowered)) return null;
  const collapsed = lowered.replace(/[\s+]+/g, '_');
  return collapsed.length === 0 ? null : collapsed;
}

/** Prefix-aware canonical-source test (`influencer_janedoe` is canonical). */
export function isCanonicalSource(source: string): boolean {
  if (UTM_SOURCES.includes(source)) return true;
  return SOURCE_PREFIXES.some((p) => source.startsWith(p) && source.length > p.length);
}

/** The matching open-ended prefix for a source, or null. */
export function sourcePrefixOf(source: string): string | null {
  return (
    SOURCE_PREFIXES.find((p) => source.startsWith(p) && source.length > p.length) ?? null
  );
}

/** Mediums the builder may offer for a source (empty when the source is unknown). */
export function mediumsForSource(source: string): readonly string[] {
  const prefix = sourcePrefixOf(source);
  if (prefix) return SOURCE_MEDIUMS[prefix] ?? [];
  return SOURCE_MEDIUMS[source] ?? [];
}

export type UtmTupleIssue =
  | 'source_missing'
  | 'source_off_taxonomy'
  | 'source_reserved'
  | 'medium_missing'
  | 'medium_off_taxonomy'
  | 'medium_not_valid_for_source'
  | 'campaign_missing'
  | 'campaign_malformed';

export interface UtmTuple {
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
}

export interface UtmTupleVerdict {
  /** No issues. At ingest this means "recognised"; at emission "safe to publish". */
  ok: boolean;
  issues: UtmTupleIssue[];
  /** The §1-normalised tuple (sentinels and blanks already collapsed to null). */
  normalized: Required<{ [K in keyof UtmTuple]: string | null }>;
}

/**
 * Check a source/medium/campaign tuple against the taxonomy.
 *
 * mode='emission' (default) — for the admin campaign-link builder. All three
 * params are REQUIRED (§1 marks them ✅), the source/medium must be canonical,
 * the medium must be one the builder offers for that source, and the campaign
 * must match the §4 slug shape. This is the strict arm, and it is strict
 * because a bad published link is unfixable after the fact.
 *
 * mode='ingest' — for POST /v1/attribution and POST /v1/waitlist. ABSENCE IS
 * NEVER AN ISSUE: a direct visit or an organic install legitimately carries no
 * UTMs at all, and warning on those would bury the real signal. Only values
 * that are PRESENT are checked, the source/medium pairing is not enforced, and
 * the provider sentinel pair (`google-play|app-store` + `organic`) is
 * explicitly recognised. The verdict here drives a log line, never a rejection.
 */
export function validateUtmTuple(
  input: UtmTuple,
  mode: 'emission' | 'ingest' = 'emission',
): UtmTupleVerdict {
  const source = normalizeTag(input.utm_source);
  const medium = normalizeTag(input.utm_medium);
  const campaign = normalizeTag(input.utm_campaign);
  const issues: UtmTupleIssue[] = [];

  // Provider sentinel pair — a real, correct, high-volume ingest shape that
  // deriveChannel() turns into `organic_app_store`. Nothing to complain about.
  const isProviderPair =
    source != null && PROVIDER_SOURCES.includes(source) && medium === PROVIDER_MEDIUM;
  if (mode === 'ingest' && isProviderPair) {
    return { ok: true, issues, normalized: { utm_source: source, utm_medium: medium, utm_campaign: campaign } };
  }

  if (source == null) {
    if (mode === 'emission') issues.push('source_missing');
  } else if (RESERVED_SOURCES.includes(source)) {
    issues.push('source_reserved');
  } else if (!isCanonicalSource(source)) {
    // Provider sources are legal at ingest but never selectable at emission.
    if (!(mode === 'ingest' && PROVIDER_SOURCES.includes(source))) {
      issues.push('source_off_taxonomy');
    }
  }

  if (medium == null) {
    if (mode === 'emission') issues.push('medium_missing');
  } else if (!(medium in MEDIUM_TO_CHANNEL)) {
    issues.push('medium_off_taxonomy');
  } else if (mode === 'emission' && source != null && !mediumsForSource(source).includes(medium)) {
    issues.push('medium_not_valid_for_source');
  }

  if (campaign == null) {
    if (mode === 'emission') issues.push('campaign_missing');
  } else if (mode === 'emission' && !CAMPAIGN_SLUG_RE.test(campaign)) {
    // Ingest deliberately does not police campaign shape: a legacy or partner
    // link with an off-shape campaign is still perfectly attributable.
    issues.push('campaign_malformed');
  }

  return {
    ok: issues.length === 0,
    issues,
    normalized: { utm_source: source, utm_medium: medium, utm_campaign: campaign },
  };
}
